import React from 'react';
import { observer } from 'mobx-react';
import { TerraceConstructionReport } from '../TerraceConstructionReport/TerraceConstructionReport';

interface ICombinedTerraceConstructionReportProps {
  terraceReports: any[];
  ugoReports: any[];
  project: any;
  isUGOSelected: boolean;
  areIndividualTerracesSelected: boolean;
  ugoData?: any[];
  selectedElements?: any[]; 
  planMapImage?: string;
  locationMapImage?: string;
}


export const CombinedTerraceConstructionReport: React.FC<ICombinedTerraceConstructionReportProps> = observer(({
  terraceReports,
  ugoReports,
  project,
  isUGOSelected,
  areIndividualTerracesSelected,
  ugoData = [],
  selectedElements = [],
  planMapImage = '',
  locationMapImage = ''
}) => {
  // Helper function to get UGO name by alignedElementDesignParamsId
  const getUgoNameById = (alignedElementDesignParamsId: number): string => {
    const selectedUgo = selectedElements.find(
      (el) => el.elementType === 'UGO' && el.alignedElementDesignParamsId === alignedElementDesignParamsId
    );
    
    if (selectedUgo) {
      return selectedUgo.ugoName || selectedUgo.designElementName;
    } 
    return '';
  };

  let primaryReports: any[] = [];
  
  if (isUGOSelected && areIndividualTerracesSelected) {
    const ugoConstructionReports = ugoData.map((ugoItem, index) => {
      const ugoId = ugoItem.alignedElementDesignParamsId || ugoItem.id;
      const actualUgoName = getUgoNameById(ugoId);
      
      return {
        terraceName: actualUgoName,
        isUGOConstructionReport: true,
        ugoIndex: index,
        projectName: terraceReports[0]?.projectName,
        projectCounty: terraceReports[0]?.projectCounty,
        practice: terraceReports[0]?.practice,
        projectDescription: terraceReports[0]?.projectDescription,
      };
    });
    
    //Terrace reports first, then UGO construction reports
    primaryReports = [...terraceReports, ...ugoConstructionReports];
  } else if (isUGOSelected) {
    // Only UGO selected - use UGO terrace reports if available, otherwise create from ugoData
    if (ugoReports.length > 0) {
      primaryReports = ugoReports;
    } else {
      // Create UGO construction reports from ugoData - only include minimal data needed
      primaryReports = ugoData.map((ugoItem, index) => {
        // Get the actual UGO name using the alignedElementDesignParamsId from ugoItem
        const ugoId = ugoItem.alignedElementDesignParamsId || ugoItem.id;
        const actualUgoName = getUgoNameById(ugoId);
        
        return {
          terraceName: actualUgoName,
          isUGOConstructionReport: true,
          ugoIndex: index,
        };
      });
    }
  } else {
    // Only individual terraces selected
    primaryReports = terraceReports;
  }

  const hasMultipleDesigns = primaryReports.length > 1;

  if (!hasMultipleDesigns) {
    // If only one design is selected, use the original single report approach
    if (isUGOSelected && ugoReports.length > 0) {
      return (
        <TerraceConstructionReport
          terraceContent={ugoReports[0]}
          project={project}
          isUGOSelected={true}
          areIndividualTerracesSelected={areIndividualTerracesSelected}
          ugoData={ugoData}
        />
      );
    } else if (areIndividualTerracesSelected && terraceReports.length > 0) {
      return (
        <TerraceConstructionReport
          terraceContent={terraceReports[0]}
          project={project}
          isUGOSelected={false}
          areIndividualTerracesSelected={true}
        />
      );
    }
  }

  // For multiple designs, create combined report
  const firstReport = primaryReports[0];
  const combinedTerraceSummaryData = firstReport.terraceSummaryInfoList || [];
  // Create a combined report object for shared pages
  const combinedReportData = {
    ...firstReport,
    terraceSummaryInfoList: combinedTerraceSummaryData,
    locationMapImage: locationMapImage || firstReport.locationMapImage,
    terracePlanViewImage: planMapImage || firstReport.terracePlanViewImage,
    image811: firstReport.image811,
    projectName: firstReport.projectName || project.name,
    projectCounty: firstReport.projectCounty || project.location,
    practice: firstReport.practice || project.practice,
    projectDescription: firstReport.projectDescription || project.description,
  };

  return (
    <div className="combined-terrace-report">
      {/* Render shared pages using the first report as template but with combined data */}
      <CombinedSharedPages
        terraceContent={combinedReportData}
        project={project}
        isUGOSelected={isUGOSelected}
        areIndividualTerracesSelected={areIndividualTerracesSelected}
      />

      {/* Render individual pages for each design */}
      {primaryReports.map((report, index) => {
        // Determine if this report is a UGO construction report or terrace report
        const isUGOConstructionReport = report.isUGOConstructionReport;
        const ugoReportIndex = isUGOConstructionReport ? report.ugoIndex : -1;
        
        // For UGO construction reports, get the corresponding UGO data
        const correspondingUgoData = isUGOConstructionReport && ugoData && ugoData[ugoReportIndex] 
          ? [ugoData[ugoReportIndex]] 
          : [];
        
        return (
          <CombinedIndividualPages
            key={`design-${index}`}
            terraceContent={report}
            project={project}
            isUGOSelected={isUGOConstructionReport}
            areIndividualTerracesSelected={!isUGOConstructionReport}
            ugoData={correspondingUgoData}
            designIndex={index}
            totalDesigns={primaryReports.length}
          />
        );
      })}
    </div>
  );
});

/**
 * Renders shared pages that appear once per combined report
 */
const CombinedSharedPages: React.FC<{
  terraceContent: any;
  project: any;
  isUGOSelected: boolean;
  areIndividualTerracesSelected: boolean;
}> = ({ terraceContent, project, isUGOSelected, areIndividualTerracesSelected }) => {
  return (
    <TerraceConstructionReport
      terraceContent={terraceContent}
      project={project}
      isUGOSelected={isUGOSelected}
      areIndividualTerracesSelected={areIndividualTerracesSelected}
      renderMode="shared-pages-only"
    />
  );
};

/**
 * Renders individual pages for each design (Construction Checkout Sheet, Profile Table, etc.)
 */
const CombinedIndividualPages: React.FC<{
  terraceContent: any;
  project: any;
  isUGOSelected: boolean;
  areIndividualTerracesSelected: boolean;
  ugoData?: any[];
  designIndex: number;
  totalDesigns: number;
}> = ({ terraceContent, project, isUGOSelected, areIndividualTerracesSelected, ugoData, designIndex, totalDesigns }) => {
  return (
    <TerraceConstructionReport
      terraceContent={terraceContent}
      project={project}
      isUGOSelected={isUGOSelected}
      areIndividualTerracesSelected={areIndividualTerracesSelected}
      ugoData={ugoData}
      renderMode="individual-pages-only"
      designIndex={designIndex}
      totalDesigns={totalDesigns}
    />
  );
};
